// Recorded chunks wait here, on disk (IndexedDB), until the server has them.
// A crash, a reload or a dropped connection mid-take loses nothing: the
// upload queue picks up where it left off next time the page opens.
// Without IndexedDB (some private modes) it falls back to memory.

export interface StoredTrack {
  id: string;
  sessionId: string;
  participantId: string;
  kind: 'audio' | 'video';
  mime: string;
  startedAtServerMs: number;
  clockOffsetMs: number | null;
  clockRttMs: number | null;
  deviceLabel: string | null;
  /** The server knows about this track. */
  registered: boolean;
  /** Set when recording stopped: the last chunk's seq (-1 for none). */
  finalSeq: number | null;
  /** Highest seq recorded so far, so a track cut off by a reload can still be closed. */
  lastSeq?: number;
  endedAtServerMs: number | null;
}

export interface ChunkStore {
  putTrack(track: StoredTrack): Promise<void>;
  deleteTrack(id: string): Promise<void>;
  tracks(): Promise<StoredTrack[]>;
  putChunk(trackId: string, seq: number, blob: Blob): Promise<void>;
  getChunk(trackId: string, seq: number): Promise<Blob | null>;
  deleteChunk(trackId: string, seq: number): Promise<void>;
  /** [trackId, seq] of every chunk still waiting. */
  chunkKeys(): Promise<Array<[string, number]>>;
  /** True when chunks survive a reload. */
  readonly durable: boolean;
}

export function memoryChunkStore(): ChunkStore {
  const tracks = new Map<string, StoredTrack>();
  const chunks = new Map<string, { trackId: string; seq: number; blob: Blob }>();
  const k = (trackId: string, seq: number) => `${trackId}:${seq}`;
  return {
    durable: false,
    putTrack: async (t) => void tracks.set(t.id, { ...t }),
    deleteTrack: async (id) => void tracks.delete(id),
    tracks: async () => [...tracks.values()].map((t) => ({ ...t })),
    putChunk: async (trackId, seq, blob) => void chunks.set(k(trackId, seq), { trackId, seq, blob }),
    getChunk: async (trackId, seq) => chunks.get(k(trackId, seq))?.blob ?? null,
    deleteChunk: async (trackId, seq) => void chunks.delete(k(trackId, seq)),
    chunkKeys: async () => [...chunks.values()].map((c): [string, number] => [c.trackId, c.seq]),
  };
}

export const DB_NAME = 'podcastwiz-studio';
const TRACKS = 'tracks';
const CHUNKS = 'chunks';

function done<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function open(idb: IDBFactory): Promise<IDBDatabase> {
  const req = idb.open(DB_NAME, 1);
  req.onupgradeneeded = () => {
    req.result.createObjectStore(TRACKS, { keyPath: 'id' });
    req.result.createObjectStore(CHUNKS, { keyPath: ['trackId', 'seq'] });
  };
  return done(req);
}

/** The on-disk store, or memory where IndexedDB isn't available. */
export async function openChunkStore(idb: IDBFactory | undefined = globalThis.indexedDB): Promise<ChunkStore> {
  if (!idb) return memoryChunkStore();
  let db: IDBDatabase;
  try {
    db = await open(idb);
  } catch {
    return memoryChunkStore();
  }
  const store = (name: string, mode: IDBTransactionMode) => db.transaction(name, mode).objectStore(name);
  return {
    durable: true,
    putTrack: async (t) => void (await done(store(TRACKS, 'readwrite').put(t))),
    deleteTrack: async (id) => void (await done(store(TRACKS, 'readwrite').delete(id))),
    tracks: async () => (await done(store(TRACKS, 'readonly').getAll())) as StoredTrack[],
    putChunk: async (trackId, seq, blob) => void (await done(store(CHUNKS, 'readwrite').put({ trackId, seq, blob }))),
    getChunk: async (trackId, seq) => ((await done(store(CHUNKS, 'readonly').get([trackId, seq]))) as { blob: Blob } | undefined)?.blob ?? null,
    deleteChunk: async (trackId, seq) => void (await done(store(CHUNKS, 'readwrite').delete([trackId, seq]))),
    chunkKeys: async () => (await done(store(CHUNKS, 'readonly').getAllKeys())) as Array<[string, number]>,
  };
}
